/**
 * TASKS-INVITE.MODAL
 * Invite manager to create their action plan
 */
import { v4 as uuid } from "uuid"
import Modal from "@/components/modal"
import { WithTranslation, withTranslation } from "react-i18next"
import { connect } from "react-redux"
import { Session } from "@/redux/_session.types"
import { User, UserState } from "@/redux/user.types"
import { useEffect, useState } from "react"
import { store } from "@/index"
import { faCaretRight } from "@fortawesome/free-solid-svg-icons"
import { surveyStatus } from "@/redux/survey.actions"
import { SurveyState } from "@/redux/survey.types"
import { userFetch, userGet } from "@/redux/user.actions"
import { STATUS_LOAD_ERROR, STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import PageLoader from "@/components/page-loader"
import { DashboardFilters } from "@/redux/filter.types"
import { Attribute } from "@/redux/attribute.types"
import fetchArchiveFilters from "@/utils/fetch-archive-filters.utils"
import fetchArchiveAttributes from "@/utils/fetch-archive-attributes.utils"
import ListItem from "@/components/list-item"
import Checkbox from "@/components/checkbox"
import Space from "@/components/space"
import { sortBy } from "lodash"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { toast } from "react-toastify"
import ModalConfirm from "@/components/modal-confirm"
import { actionPlanUpdate } from "@/redux/action-plan.actions"
import LoadingModal from "./loading.modal"
import { ActionPlan, ActionPlanState } from "@/redux/action-plan.types"
import SurveySelectWidget from "@/widgets/survey-select.widget"

interface StateProps extends WithTranslation {
  _session: Session
  actionPlan: ActionPlanState
  user: UserState
  survey: SurveyState
}

interface OwnProps {
  onClose: Function
}

type Props = StateProps & OwnProps

const MODAL_MANAGERS = "MODAL_MANAGERS"
const MODAL_MANAGERS_CONFIRM = "MODAL_MANAGERS_CONFIRM"

function TasksInviteModal(props: Props) {
  const { t } = props

  const [currentModal, setCurrentModal] = useState<null | string>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [sendEmail, setSendEmail] = useState(true)
  const [selectedManagers, setSelectedManagers] = useState<string[]>([])

  //Load managers
  useEffect(() => {
    load()
    async function load() {
      store.dispatch(surveyStatus(STATUS_LOADING))

      //Fetch users
      const users: any = await store.dispatch(
        userFetch("", null, null, ["OBSERVER"], "id,email,options,firstname,lastname,populationsObserver")
      )

      if (users.error) {
        store.dispatch(surveyStatus(STATUS_LOAD_ERROR))
      } else {
        //Fetch filters and attributes
        const filters = await fetchArchiveFilters(new DashboardFilters(), props.survey.active.id)
        const attributes: Attribute[] = await fetchArchiveAttributes(
          new DashboardFilters(),
          props.survey.active.id,
          null
        )

        //Format manager
        const managers: User[] = []
        for (let i = 0; i < users.length; i++) {
          const user = users[i]
          let show = false

          for (let j = 0; j < user.populationsObserver.length; j++) {
            const population = user.populationsObserver[j]
            if (
              attributes.find((x) => x.name === population.name) &&
              filters.find((x) => x.name === population.filterName)
            ) {
              show = true
            }
          }

          if (show) {
            managers.push(user)
          }
        }

        //End loading
        const managersWithActionPlan = props.actionPlan.list.map((x) => x.UserId)
        setSelectedManagers(managers.filter((x) => managersWithActionPlan.indexOf(x.id) === -1).map((x) => x.id))
        store.dispatch(userGet(managers))
        store.dispatch(surveyStatus(STATUS_LOADED))
      }
    }
  }, [props.survey.active.id])

  async function create() {
    setIsSaving(true)

    for (let i = 0; i < getSelectedManagers().length; i++) {
      const manager = getSelectedManagers()[i]
      await store.dispatch(
        actionPlanUpdate(
          new ActionPlan({
            id: uuid(),
            SurveyId: props.survey.active.id,
            UserId: manager.id
          }),
          sendEmail,
          props._session.userName,
          manager.getInvitationLink(props._session, "tasks")
        )
      )
    }

    window.location.reload()
    props.onClose()
  }

  function confirmManagers() {
    if (selectedManagers.length === 0) {
      toast(t("roles_observer_empty"), { type: "error" })
    } else {
      setCurrentModal(MODAL_MANAGERS_CONFIRM)
    }
  }

  function getSelectedManagers() {
    return props.user.list.filter((x) => selectedManagers.includes(x.id))
  }

  function getConfirmText() {
    const managers = getSelectedManagers()
      .map((x) => x.username)
      .join(", ")

    return t("roles_observer_send") + managers + "."
  }

  return (
    <Modal
      isCloseButtonVisible
      isLarge
      title={t("tasks_invite_managers")}
      onClose={() => props.onClose()}
      onNext={() => confirmManagers()}>
      {isSaving && <LoadingModal />}

      {currentModal === MODAL_MANAGERS_CONFIRM && (
        <ModalConfirm
          onNo={() => setCurrentModal(MODAL_MANAGERS)}
          onYes={() => create()}
          textBold={t("utils_next_ask")}
          text={getConfirmText()}>
          <div className="flex mb-40">
            <Checkbox active={sendEmail} onClick={() => setSendEmail(!sendEmail)} text={t("tasks_invite_send_email")} />
            <Space />
          </div>
        </ModalConfirm>
      )}

      <SurveySelectWidget />

      <div className="modal-content" style={{ height: 300 }}>
        <PageLoader status={props.survey.status}>
          {props.user.list.length === 0 ? (
            <div className="grey-t">{t("roles_observer_empty")}</div>
          ) : (
            <ListItem>
              <Checkbox
                active={selectedManagers.length === props.user.list.length}
                onClick={() =>
                  setSelectedManagers(
                    selectedManagers.length === props.user.list.length ? [] : props.user.list.map((x) => x.id)
                  )
                }
              />
              <div className="w-20" />
              <span className="grey-t">Sélectionner tous les managers</span>
              <Space />
            </ListItem>
          )}
          {props.user.list.map((user) => (
            <ListItem key={user.id}>
              <Checkbox
                active={selectedManagers.includes(user.id)}
                onClick={() =>
                  setSelectedManagers(
                    selectedManagers.includes(user.id)
                      ? selectedManagers.filter((x) => x !== user.id)
                      : selectedManagers.concat([user.id])
                  )
                }
              />
              <div className="w-20" />
              <div className="w-[180px]">
                <div>{user.username}</div>
                <div className="grey-t" style={{ fontSize: 12, marginTop: 4 }}>
                  {user.email}
                </div>
              </div>
              <Space />
              <div className="w-20" />
              <div className="w-[300px]">
                {sortBy(user.populationsObserver, "name").map((population, i) => (
                  <div key={i} className="grey-t">
                    <FontAwesomeIcon icon={faCaretRight} />
                    &nbsp;
                    {population.filterName && <span>{population.filterName} : </span>}
                    <b>{population.name}</b>
                  </div>
                ))}
              </div>
            </ListItem>
          ))}
        </PageLoader>
      </div>
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  actionPlan: state.actionPlan,
  user: state.user,
  survey: state.survey
})

export default connect(mapStateToProps)(withTranslation()(TasksInviteModal))
